/**
 * StripeIntegrationContent
 *
 * Reusable content component for Stripe Connect integration.
 * Shows two separate sections for Test and Live mode connections.
 * Each mode requires its own OAuth flow due to Stripe Connect limitations.
 *
 * @module stripe-connect/components/StripeIntegrationContent
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import AppleIcon from '@mui/icons-material/Apple';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import { StyledTextField, WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS, type GuideAnchorDefinition } from '@modules/guides/anchors/guideAnchorRegistry';
import { ConnectStripeButton } from './ConnectStripeButton';
import { useStripeConnect } from '../hooks/useStripeConnect';
import { STRIPE_CONNECT_TEST_IDS } from '../testIds';
import type { StripeAccountStatusResponse, StripePaymentMethodDomain } from '../types';

export interface StripeIntegrationContentProps {
  /** Project ID to load Stripe config for */
  projectId: string;
  /** Whether to show header title and description */
  showHeader?: boolean;
}

/**
 * Stripe logo SVG component
 */
const StripeLogo: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.976 12.312c0-.935.78-1.295 2.058-1.295 1.841 0 4.167.555 6.008 1.55V6.832c-2.013-.805-4.006-1.12-6.008-1.12-4.913 0-8.18 2.564-8.18 6.844 0 6.672 9.184 5.606 9.184 8.484 0 1.107-.97 1.467-2.321 1.467-2.006 0-4.58-.825-6.608-1.934v5.79c2.249.972 4.526 1.38 6.608 1.38 5.031 0 8.49-2.494 8.49-6.824-.018-7.202-9.231-5.919-9.231-8.627z"
      fill="#635BFF"
    />
  </svg>
);

/**
 * Status indicator for account capabilities
 */
const StatusIndicator: React.FC<{
  label: string;
  enabled: boolean;
  tooltip?: string;
}> = ({ label, enabled, tooltip }) => {
  const theme = useTheme();

  const content = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {enabled ? (
        <CheckCircleIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
      ) : (
        <ErrorIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
      )}
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );

  return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
};

const ApplePayDomainsSection: React.FC<{
  mode: 'test' | 'live';
  domains: StripePaymentMethodDomain[];
  loading: boolean;
  onLoad: (mode: 'test' | 'live') => Promise<void>;
  onRegister: (mode: 'test' | 'live', domainName: string) => Promise<StripePaymentMethodDomain>;
}> = ({ mode, domains, loading, onLoad, onRegister }) => {
  const [domainName, setDomainName] = useState('');
  const [registering, setRegistering] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);

  useEffect(() => {
    void onLoad(mode);
  }, [mode, onLoad]);

  const handleRegister = async () => {
    const trimmed = domainName.trim();
    if (!trimmed) {
      setDomainError('Domain is required');
      return;
    }

    setRegistering(true);
    setDomainError(null);

    try {
      await onRegister(mode, trimmed);
      setDomainName('');
    } catch (err) {
      setDomainError(err instanceof Error ? err.message : 'Failed to register domain');
    } finally {
      setRegistering(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        pt: 2,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <AppleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" fontWeight={600}>
          Apple Pay Domains
        </Typography>
        <Tooltip title="Refresh domains">
          <span>
            <IconButton
              size="small"
              onClick={() => void onLoad(mode)}
              disabled={loading || registering}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
        <StyledTextField
          value={domainName}
          onChange={setDomainName}
          size="small"
          type="url"
          placeholder="checkout.example.com"
          fullWidth
          error={Boolean(domainError)}
          helperText={domainError || ' '}
          disabled={registering}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              void handleRegister();
            }
          }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={registering ? <CircularProgress size={14} /> : <AddIcon fontSize="small" />}
          onClick={() => void handleRegister()}
          disabled={registering || loading}
          sx={{ mt: 0.25, minWidth: 112 }}
        >
          Register
        </Button>
      </Box>

      {loading && domains.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Loading domains
          </Typography>
        </Box>
      ) : domains.length > 0 ? (
        <Box sx={{ display: 'grid', gap: 1 }}>
          {domains.map((domain) => {
            const isApplePayActive = domain.enabled && domain.applePayStatus === 'active';
            return (
              <Box
                key={domain.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontFamily: 'monospace',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {domain.domainName}
                </Typography>
                <Tooltip title={domain.applePayError || ''}>
                  <Chip
                    size="small"
                    color={isApplePayActive ? 'success' : 'warning'}
                    variant="outlined"
                    label={isApplePayActive ? 'Apple Pay active' : 'Apple Pay inactive'}
                  />
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">
          No domains registered for this Stripe account.
        </Typography>
      )}
    </Box>
  );
};

/**
 * Mode-specific connection card component
 */
interface ModeConnectionCardProps {
  mode: 'test' | 'live';
  status: StripeAccountStatusResponse | null;
  projectId: string;
  loading: boolean;
  onConnected: () => void;
  onDisconnect: () => Promise<void>;
  onRefresh: () => void;
  onError: (error: string) => void;
  guideAnchor: GuideAnchorDefinition;
  guideService: GuideService;
  paymentMethodDomains: StripePaymentMethodDomain[];
  loadingPaymentMethodDomains: boolean;
  onLoadPaymentMethodDomains: (mode: 'test' | 'live') => Promise<void>;
  onRegisterPaymentMethodDomain: (
    mode: 'test' | 'live',
    domainName: string
  ) => Promise<StripePaymentMethodDomain>;
}

const ModeConnectionCard: React.FC<ModeConnectionCardProps> = ({
  mode,
  status,
  projectId,
  loading,
  onConnected,
  onDisconnect,
  onRefresh,
  onError,
  guideAnchor,
  guideService,
  paymentMethodDomains,
  loadingPaymentMethodDomains,
  onLoadPaymentMethodDomains,
  onRegisterPaymentMethodDomain,
}) => {
  const theme = useTheme();
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);

  const isTest = mode === 'test';
  const isConnected = status?.connected;
  const hasIssues = isConnected && (!status?.chargesEnabled || !status?.payoutsEnabled);

  const handleDisconnectClick = () => {
    setDisconnectDialogOpen(true);
  };

  const handleDisconnectConfirm = async () => {
    setDisconnecting(true);
    setDisconnectDialogOpen(false);
    try {
      await onDisconnect();
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      data-testid={STRIPE_CONNECT_TEST_IDS.card(mode)}
      sx={{
        p: 3,
        borderColor: isConnected
          ? hasIssues
            ? theme.palette.warning.main
            : theme.palette.success.main
          : theme.palette.divider,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Chip
          label={isTest ? 'TEST' : 'LIVE'}
          color={isTest ? 'warning' : 'success'}
          size="small"
          data-testid={STRIPE_CONNECT_TEST_IDS.statusChip(mode)}
          sx={{ fontWeight: 600 }}
        />
        <WithGuideHelp
          guideKey={guideAnchor.guideKey}
          guideService={guideService}
          data-testid={guideAnchor.testId}
          position="inline-end"
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isTest ? 'Test Mode' : 'Live Mode'}
          </Typography>
        </WithGuideHelp>
      </Box>

      {/* Description */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isTest
          ? 'Use test cards (4242 4242 4242 4242) for development and testing.'
          : 'Process real payments with actual credit cards.'}
      </Typography>

      {/* Content */}
      <Box sx={{ flex: 1 }}>
        {loading && !status ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isConnected ? (
          <Card
            variant="outlined"
            sx={{
              borderColor: hasIssues
                ? theme.palette.warning.main
                : theme.palette.success.light,
            }}
          >
            <CardContent sx={{ pb: '16px !important' }}>
              {/* Account Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <StripeLogo size={24} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Connected
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {status.accountId}
                  </Typography>
                </Box>
                <Tooltip title="Refresh status">
                  <IconButton
                    size="small"
                    onClick={onRefresh}
                    disabled={loading}
                    data-testid={STRIPE_CONNECT_TEST_IDS.refreshBtn(mode)}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Status Indicators */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <StatusIndicator
                  label="Charges"
                  enabled={status.chargesEnabled ?? false}
                  tooltip={
                    status.chargesEnabled
                      ? 'Can accept payments'
                      : 'Complete onboarding to accept payments'
                  }
                />
                <StatusIndicator
                  label="Payouts"
                  enabled={status.payoutsEnabled ?? false}
                  tooltip={
                    status.payoutsEnabled
                      ? 'Can receive payouts'
                      : 'Add bank account to receive payouts'
                  }
                />
              </Box>

              {/* Warning */}
              {hasIssues && (
                <Alert severity="warning" icon={<WarningIcon fontSize="small" />} sx={{ mb: 2 }}>
                  <Typography variant="caption">
                    Complete setup in{' '}
                    <a
                      href={`https://dashboard.stripe.com${isTest ? '/test' : ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: theme.palette.primary.main }}
                    >
                      Stripe Dashboard
                    </a>
                  </Typography>
                </Alert>
              )}

              <ApplePayDomainsSection
                mode={mode}
                domains={paymentMethodDomains}
                loading={loadingPaymentMethodDomains}
                onLoad={onLoadPaymentMethodDomains}
                onRegister={onRegisterPaymentMethodDomain}
              />

              {/* Disconnect Button */}
              <Button
                variant="text"
                color="error"
                size="small"
                startIcon={
                  disconnecting ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <LinkOffIcon fontSize="small" />
                  )
                }
                onClick={handleDisconnectClick}
                disabled={disconnecting}
                fullWidth
                data-testid={STRIPE_CONNECT_TEST_IDS.disconnectBtn(mode)}
              >
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </Button>

              {/* Connected timestamp */}
              {status.connectedAt && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', textAlign: 'center', mt: 1 }}
                >
                  Connected {new Date(status.connectedAt).toLocaleDateString()}
                </Typography>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Not Connected State */
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <StripeLogo size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
              No {isTest ? 'test' : 'live'} account connected
            </Typography>
            <ConnectStripeButton
              projectId={projectId}
              mode={mode}
              onConnected={onConnected}
              onError={onError}
              variant="contained"
              fullWidth
              data-testid={STRIPE_CONNECT_TEST_IDS.connectBtn(mode)}
            />
          </Box>
        )}
      </Box>

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={disconnectDialogOpen}
        onClose={() => setDisconnectDialogOpen(false)}
      >
        <DialogTitle>
          Disconnect {isTest ? 'Test' : 'Live'} Account?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isTest ? (
              <Typography variant="body2">
                Disconnecting your test account will disable test payments.
                You can reconnect anytime.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="error.main" fontWeight={600} gutterBottom>
                  ⚠️ This will stop accepting real payments!
                </Typography>
                <Typography variant="body2">
                  Published funnels will not be able to process live payments until you reconnect.
                </Typography>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDisconnectConfirm}
            color="error"
            variant="contained"
            data-testid={STRIPE_CONNECT_TEST_IDS.disconnectConfirmBtn}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

/**
 * StripeIntegrationContent Component
 *
 * Displays Stripe Connect integration UI with two sections:
 * - Test Mode connection (for development)
 * - Live Mode connection (for production)
 *
 * Each mode requires its own OAuth flow because Stripe Connect Standard
 * accounts are mode-specific.
 */
export function StripeIntegrationContent({
  projectId,
  showHeader = false,
}: StripeIntegrationContentProps) {
  const guideService = useMemo(() => new GuideService(), []);
  const {
    testStatus,
    liveStatus,
    loading,
    error,
    refreshTestStatus,
    refreshLiveStatus,
    disconnectTest,
    disconnectLive,
    paymentMethodDomains,
    loadingPaymentMethodDomains,
    fetchPaymentMethodDomains,
    registerPaymentMethodDomain,
  } = useStripeConnect(projectId);

  const handleTestConnected = useCallback(() => {
    refreshTestStatus();
  }, [refreshTestStatus]);

  const handleLiveConnected = useCallback(() => {
    refreshLiveStatus();
  }, [refreshLiveStatus]);

  const handleError = useCallback((errorMessage: string) => {
    console.error('Stripe connection error:', errorMessage);
  }, []);

  // Show loading state only on initial load
  const isInitialLoading = loading && !testStatus && !liveStatus;

  if (isInitialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Optional Header */}
      {showHeader && (
        <>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Stripe Integration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect your Stripe account to accept payments in your onboarding funnels.
          </Typography>
        </>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Two-Section Layout: Test Mode and Live Mode */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          mb: 3,
        }}
      >
        {/* Test Mode Section */}
        <ModeConnectionCard
          mode="test"
          status={testStatus}
          projectId={projectId}
          loading={loading}
          onConnected={handleTestConnected}
          onDisconnect={disconnectTest}
          onRefresh={refreshTestStatus}
          onError={handleError}
          guideAnchor={GUIDE_ANCHORS.stripeConnectTestMode}
          guideService={guideService}
          paymentMethodDomains={paymentMethodDomains.test}
          loadingPaymentMethodDomains={loadingPaymentMethodDomains.test}
          onLoadPaymentMethodDomains={fetchPaymentMethodDomains}
          onRegisterPaymentMethodDomain={registerPaymentMethodDomain}
        />

        {/* Live Mode Section */}
        <ModeConnectionCard
          mode="live"
          status={liveStatus}
          projectId={projectId}
          loading={loading}
          onConnected={handleLiveConnected}
          onDisconnect={disconnectLive}
          onRefresh={refreshLiveStatus}
          onError={handleError}
          guideAnchor={GUIDE_ANCHORS.stripeConnectLiveMode}
          guideService={guideService}
          paymentMethodDomains={paymentMethodDomains.live}
          loadingPaymentMethodDomains={loadingPaymentMethodDomains.live}
          onLoadPaymentMethodDomains={fetchPaymentMethodDomains}
          onRegisterPaymentMethodDomain={registerPaymentMethodDomain}
        />
      </Box>

      {/* Info about Stripe Connect */}
      <Alert severity="info" sx={{ textAlign: 'left' }}>
        <AlertTitle>
          <WithGuideHelp
            guideKey={GUIDE_ANCHORS.stripeConnectOauthGuidance.guideKey}
            guideService={guideService}
            data-testid={GUIDE_ANCHORS.stripeConnectOauthGuidance.testId}
            position="inline-end"
          >
            Why Two Connections?
          </WithGuideHelp>
        </AlertTitle>
        <Typography variant="body2" paragraph>
          Stripe Connect Standard accounts require separate OAuth connections for test and live modes.
          Each mode has its own account ID and API keys.
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          <Typography component="li" variant="body2">
            <strong>Test Mode:</strong> For development - uses test cards (4242 4242 4242 4242)
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Live Mode:</strong> For production - processes real payments
          </Typography>
        </Box>
      </Alert>
    </Box>
  );
}

export default StripeIntegrationContent;
